import { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut, RefreshCw, Trash2, Search, Mail, Phone,
    Building2, ChevronDown, Loader2, BadgeCheck, Inbox,
    Truck, Warehouse, MessageSquare, Briefcase, Sparkles, Download,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    adminFetchLeads, adminFetchStats, adminLogout, adminUpdateLeadStatus, adminDeleteLead, getToken,
    adminDownloadCsv,
} from "@/lib/admin";
import JobsManager from "@/components/admin/JobsManager";

const LEAD_TABS = [
    { value: "all", label: "All Submissions", icon: Inbox, hint: "Every form" },
    { value: "consultation", label: "Consultation", icon: Sparkles, hint: "Supply-chain consults" },
    { value: "transportation", label: "Transportation", icon: Truck, hint: "Transport quotes" },
    { value: "warehousing", label: "Warehousing", icon: Warehouse, hint: "Warehouse proposals" },
    { value: "contact", label: "Contact", icon: MessageSquare, hint: "General contact" },
    { value: "career", label: "Career", icon: Briefcase, hint: "Job applications" },
];

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const STATUS_COLOR = {
    new: "bg-[#F4A000] text-[#101A2C]",
    contacted: "bg-[#4B6390] text-white",
    qualified: "bg-[#28406A] text-white",
    won: "bg-emerald-600 text-white",
    lost: "bg-zinc-500 text-white",
};

function fmtDate(s) {
    try {
        return new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
        return s;
    }
}

function StatCard({ label, value, testId }) {
    return (
        <div className="bg-white border border-[#CBD5E1] p-5" data-testid={testId}>
            <p className="overline">{label}</p>
            <p className="font-display text-4xl text-[#101A2C] mt-2">{value}</p>
        </div>
    );
}

function LeadRow({ lead, onChange, onDelete, expanded, onToggle }) {
    const p = lead.payload || {};
    const name = p.name || "—";
    const company = p.company || "—";
    const email = p.email || "—";
    const phone = p.phone || p.mobile || "—";

    return (
        <>
            <tr
                className="border-t border-[#CBD5E1] hover:bg-[#F8FAFC] cursor-pointer"
                onClick={onToggle}
                data-testid={`lead-row-${lead.id}`}
            >
                <td className="p-4">
                    <span className={`inline-block px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase ${STATUS_COLOR[lead.status] || "bg-[#E9EEF3] text-[#101A2C]"}`}>
                        {lead.status}
                    </span>
                </td>
                <td className="p-4 font-semibold text-sm text-[#101A2C]">{name}</td>
                <td className="p-4 text-sm text-[#4B6390]">{company}</td>
                <td className="p-4">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-[#28406A]">
                        {lead.type}
                    </span>
                </td>
                <td className="p-4 text-xs text-[#4B6390] whitespace-nowrap">{fmtDate(lead.created_at)}</td>
                <td className="p-4 text-right">
                    <ChevronDown
                        className={`h-4 w-4 inline-block text-[#28406A] transition-transform ${expanded ? "rotate-180" : ""}`}
                        strokeWidth={1.75}
                    />
                </td>
            </tr>
            {expanded && (
                <tr className="bg-[#F8FAFC] border-t border-[#CBD5E1]" data-testid={`lead-detail-${lead.id}`}>
                    <td colSpan={6} className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <p className="overline mb-3">Contact</p>
                                <ul className="space-y-2 text-sm text-[#101A2C]">
                                    <li className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-[#F4A000]" /> {email}</li>
                                    <li className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-[#F4A000]" /> {phone}</li>
                                    <li className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-[#F4A000]" /> {company}</li>
                                </ul>
                            </div>
                            <div className="md:col-span-2">
                                <p className="overline mb-3">Submission Payload</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                    {Object.entries(p).filter(([k, v]) => v && !["name", "email", "phone", "mobile", "company"].includes(k)).map(([k, v]) => {
                                        const isResume = k === "resume_url" && typeof v === "string" && v.length > 0;
                                        const fullUrl = isResume
                                            ? (v.startsWith("http") ? v : `${process.env.REACT_APP_BACKEND_URL || ""}${v}`)
                                            : null;
                                        return (
                                            <div key={k} className="bg-white border border-[#CBD5E1] p-3">
                                                <p className="text-[10px] uppercase tracking-widest text-[#4B6390] mb-1">{k.replace(/_/g, " ")}</p>
                                                {isResume ? (
                                                    <a
                                                        href={fullUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-[#28406A] font-bold underline break-words"
                                                        data-testid={`lead-resume-link`}
                                                    >
                                                        Download Resume →
                                                    </a>
                                                ) : (
                                                    <p className="text-[#101A2C] break-words">{String(v)}</p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#CBD5E1] gap-4 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs uppercase tracking-widest text-[#4B6390] font-semibold mr-2">Status:</span>
                                {STATUSES.map((s) => (
                                    <button
                                        key={s}
                                        onClick={(e) => { e.stopPropagation(); onChange(lead.id, s); }}
                                        data-testid={`status-btn-${s}-${lead.id}`}
                                        className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border ${
                                            lead.status === s
                                                ? `${STATUS_COLOR[s]} border-transparent`
                                                : "border-[#CBD5E1] text-[#4B6390] hover:border-[#28406A] hover:text-[#28406A] bg-white"
                                        }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={(e) => { e.stopPropagation(); onDelete(lead.id); }}
                                data-testid={`delete-lead-${lead.id}`}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-bold text-red-700 border border-red-200 hover:bg-red-50"
                            >
                                <Trash2 className="h-3.5 w-3.5" /> Delete
                            </button>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

export default function AdminDashboard() {
    const navigate = useNavigate();
    const token = getToken();
    const [section, setSection] = useState("leads"); // 'leads' | 'jobs'
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [expanded, setExpanded] = useState(null);
    const [exporting, setExporting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [data, st] = await Promise.all([
                adminFetchLeads(filter),
                adminFetchStats(),
            ]);
            setLeads(data);
            setStats(st);
        } catch (err) {
            if (err?.response?.status === 401) {
                toast.error("Session expired. Please sign in again.");
                navigate("/admin", { replace: true });
            } else {
                toast.error("Could not load leads.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return leads.filter((l) => {
            if (statusFilter !== "all" && l.status !== statusFilter) return false;
            if (!q) return true;
            const p = l.payload || {};
            const hay = `${p.name || ""} ${p.company || ""} ${p.email || ""} ${p.phone || ""} ${p.mobile || ""} ${l.type}`.toLowerCase();
            return hay.includes(q);
        });
    }, [leads, statusFilter, search]);

    const onStatusChange = async (id, status) => {
        try {
            const updated = await adminUpdateLeadStatus(id, status);
            setLeads((ls) => ls.map((l) => (l.id === id ? updated : l)));
            toast.success(`Lead marked ${status}.`);
        } catch {
            toast.error("Could not update status.");
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this lead permanently?")) return;
        try {
            await adminDeleteLead(id);
            setLeads((ls) => ls.filter((l) => l.id !== id));
            toast.success("Lead deleted.");
        } catch {
            toast.error("Could not delete.");
        }
    };

    const onLogout = async () => {
        await adminLogout();
        navigate("/admin", { replace: true });
    };

    const onExportCsv = async () => {
        setExporting(true);
        try {
            await adminDownloadCsv(filter);
            toast.success(`CSV exported (${filter === "all" ? "all" : filter} leads).`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not export CSV.");
        } finally {
            setExporting(false);
        }
    };

    if (!token) {
        return <Navigate to="/admin" replace />;
    }

    return (
        <section data-testid="admin-dashboard" className="bg-[#F8FAFC] min-h-screen pb-20">
            {/* Admin top bar */}
            <div className="bg-[#101A2C] text-white border-b-4 border-[#F4A000]">
                <div className="container-vl py-6 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <p className="overline mb-1">Vibhuti Logistics · Admin</p>
                        <h1 className="font-display uppercase text-2xl md:text-3xl whitespace-nowrap">
                            {section === "leads" ? "Lead Management" : "Job Openings"}
                        </h1>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                        <button
                            onClick={() => setSection("leads")}
                            data-testid="section-leads"
                            className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 text-xs uppercase tracking-widest font-bold border ${
                                section === "leads" ? "bg-white text-[#101A2C] border-white" : "text-white border-[#4B6390] hover:bg-[#28406A]"
                            }`}
                        >
                            <Inbox className="h-4 w-4" /> Leads
                        </button>
                        <button
                            onClick={() => setSection("jobs")}
                            data-testid="section-jobs"
                            className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 text-xs uppercase tracking-widest font-bold border ${
                                section === "jobs" ? "bg-white text-[#101A2C] border-white" : "text-white border-[#4B6390] hover:bg-[#28406A]"
                            }`}
                        >
                            <Briefcase className="h-4 w-4" /> Jobs
                        </button>
                        <span className="w-px h-7 bg-[#4B6390] hidden sm:inline-block" />
                        {section === "leads" && (
                            <button
                                onClick={load}
                                data-testid="admin-refresh"
                                className="inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 border border-[#4B6390] text-xs uppercase tracking-widest font-bold hover:bg-[#28406A]"
                            >
                                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                                <span className="hidden sm:inline">Refresh</span>
                            </button>
                        )}
                        <button
                            onClick={onLogout}
                            data-testid="admin-logout"
                            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-[#F4A000] text-[#101A2C] text-xs uppercase tracking-widest font-bold hover:bg-[#FBC55A]"
                        >
                            <LogOut className="h-4 w-4" />
                            <span className="hidden sm:inline">Logout</span>
                        </button>
                    </div>
                </div>
            </div>

            {section === "jobs" ? (
                <div className="container-vl pt-8">
                    <JobsManager />
                </div>
            ) : (
            <div className="container-vl pt-8">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
                    <StatCard label="Total" value={stats?.total ?? "—"} testId="stat-total" />
                    <StatCard label="Last 7 days" value={stats?.recent_7d ?? "—"} testId="stat-recent" />
                    {Object.entries(stats?.by_type || {}).map(([k, v]) => (
                        <StatCard key={k} label={k} value={v} testId={`stat-${k}`} />
                    ))}
                </div>

                {/* Type Tabs */}
                <Tabs value={filter} onValueChange={setFilter} className="w-full">
                    <TabsList
                        className="w-full grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 bg-white border border-[#CBD5E1] p-0 h-auto rounded-none gap-px"
                        data-testid="admin-type-tabs"
                    >
                        {LEAD_TABS.map((t) => {
                            const count = t.value === "all"
                                ? (stats?.total ?? 0)
                                : (stats?.by_type?.[t.value] ?? 0);
                            const Icon = t.icon;
                            return (
                                <TabsTrigger
                                    key={t.value}
                                    value={t.value}
                                    data-testid={`tab-type-${t.value}`}
                                    className="group rounded-none flex flex-col items-start gap-1 sm:gap-1.5 p-3 sm:p-4 text-left h-auto data-[state=active]:bg-[#28406A] data-[state=active]:text-white data-[state=active]:shadow-none border-r border-b border-[#CBD5E1] last:border-r-0 min-w-0"
                                >
                                    <div className="flex items-center gap-1.5 sm:gap-2 w-full min-w-0">
                                        <Icon className="h-4 w-4 text-[#F4A000] group-data-[state=active]:text-[#F4A000] flex-shrink-0" strokeWidth={1.75} />
                                        <span className="font-display uppercase tracking-wider text-[10px] sm:text-xs flex-1 truncate">{t.label}</span>
                                        <span
                                            className="inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 text-[10px] font-bold bg-[#F4A000] text-[#101A2C] flex-shrink-0"
                                            data-testid={`tab-count-${t.value}`}
                                        >
                                            {count}
                                        </span>
                                    </div>
                                    <span className="hidden sm:block text-[10px] uppercase tracking-widest text-[#4B6390] group-data-[state=active]:text-[#CBD5E1] truncate w-full">
                                        {t.hint}
                                    </span>
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>

                    {LEAD_TABS.map((t) => (
                        <TabsContent
                            forceMount
                            key={t.value}
                            value={t.value}
                            className={`mt-6 ${filter !== t.value ? "hidden" : ""}`}
                            data-testid={`tab-content-${t.value}`}
                        >
                            <TabPanel
                                tabConfig={t}
                                leads={filtered}
                                loading={loading}
                                search={search}
                                setSearch={setSearch}
                                statusFilter={statusFilter}
                                setStatusFilter={setStatusFilter}
                                expanded={expanded}
                                setExpanded={setExpanded}
                                onStatusChange={onStatusChange}
                                onDelete={onDelete}
                                onExportCsv={onExportCsv}
                                exporting={exporting}
                            />
                        </TabsContent>
                    ))}
                </Tabs>
            </div>
            )}
        </section>
    );
}

function TabPanel({
    tabConfig, leads, loading, search, setSearch, statusFilter, setStatusFilter,
    expanded, setExpanded, onStatusChange, onDelete, onExportCsv, exporting,
}) {
    const Icon = tabConfig.icon;
    return (
        <div>
            {/* Search + status filter */}
            <div className="bg-white border border-[#CBD5E1] p-3 sm:p-4 mb-4 flex flex-wrap items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-3 pr-3 sm:pr-4 sm:border-r border-[#CBD5E1]">
                    <span className="w-9 h-9 inline-flex items-center justify-center bg-[#28406A] text-white flex-shrink-0">
                        <Icon className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0">
                        <p className="font-display uppercase text-sm text-[#101A2C] truncate">{tabConfig.label}</p>
                        <p className="text-[10px] uppercase tracking-widest text-[#4B6390]">{leads.length} record{leads.length === 1 ? "" : "s"}</p>
                    </div>
                </div>
                <div className="relative flex-1 min-w-[180px] sm:min-w-[220px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B6390]" />
                    <input
                        type="text"
                        placeholder="Search…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="admin-search"
                        className="input-vl pl-10"
                    />
                </div>
                <div className="flex items-center gap-2 flex-wrap" data-testid="admin-status-filter">
                    <span className="text-[10px] uppercase tracking-widest text-[#4B6390] font-semibold">Status:</span>
                    {["all", ...STATUSES].map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(s)}
                            data-testid={`filter-status-${s}`}
                            className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold border ${
                                statusFilter === s
                                    ? "bg-[#F4A000] text-[#101A2C] border-[#F4A000]"
                                    : "border-[#CBD5E1] text-[#4B6390] hover:border-[#28406A] hover:text-[#28406A]"
                            }`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
                <button
                    onClick={onExportCsv}
                    disabled={exporting || leads.length === 0}
                    data-testid="csv-export-btn"
                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 bg-[#28406A] text-white text-[10px] uppercase tracking-widest font-bold hover:bg-[#101A2C] disabled:opacity-50 whitespace-nowrap"
                >
                    {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                    Export CSV
                </button>
            </div>

            {/* Table */}
            <div className="bg-white border border-[#CBD5E1] overflow-x-auto" data-testid="admin-leads-table">
                <table className="w-full">
                    <thead>
                        <tr className="bg-[#E9EEF3] text-[10px] uppercase tracking-[0.2em] text-[#4B6390]">
                            <th className="p-4 text-left">Status</th>
                            <th className="p-4 text-left">Name</th>
                            <th className="p-4 text-left">Company</th>
                            <th className="p-4 text-left">Type</th>
                            <th className="p-4 text-left">Submitted</th>
                            <th className="p-4 text-right"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={6} className="p-10 text-center text-[#4B6390]">
                                <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Loading leads…
                            </td></tr>
                        )}
                        {!loading && leads.length === 0 && (
                            <tr><td colSpan={6} className="p-10 text-center text-[#4B6390]" data-testid="empty-leads">
                                <BadgeCheck className="h-6 w-6 inline-block mr-2 text-[#F4A000]" />
                                No {tabConfig.label.toLowerCase()} match your filters.
                            </td></tr>
                        )}
                        {!loading && leads.map((lead) => (
                            <LeadRow
                                key={lead.id}
                                lead={lead}
                                expanded={expanded === lead.id}
                                onToggle={() => setExpanded(expanded === lead.id ? null : lead.id)}
                                onChange={onStatusChange}
                                onDelete={onDelete}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
